import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, ArrowLeft, RefreshCw, Lock, Save, ListChecks, Activity, AlertTriangle, CalendarDays, Plus, Trophy, RotateCcw, Link2, Copy, ExternalLink, Mail, MailX, Settings as SettingsIcon, Database, Users, Printer, Cog, Webhook, ChevronDown, ChevronRight, Clock, Sunrise, Briefcase, Upload } from "lucide-react";
import api, { getToken, getLevel, clearSession } from "@/lib/api";
import { toast } from "sonner";
import OrientationDatesPanel from "@/components/OrientationDatesPanel";
import RafflesPanel from "@/components/RafflesPanel";
import TierEmailsPanel from "@/components/TierEmailsPanel";
import LateArrivalPanel from "@/components/LateArrivalPanel";
import CareRosterPanel from "@/components/CareRosterPanel";
import VendorRosterPanel from "@/components/VendorRosterPanel";
import SheetSyncPanel from "@/components/SheetSyncPanel";
import MissingStaffDiagnostic from "@/components/MissingStaffDiagnostic";
import CampminderPlannedAbsencesPanel from "@/components/CampminderPlannedAbsencesPanel";
import PrinterSetupPanel from "@/components/PrinterSetupPanel";
import PhotoLibraryPanel from "@/components/PhotoLibraryPanel";
import StaffPhotoImportPanel from "@/components/StaffPhotoImportPanel";
import NukeTestDataPanel from "@/components/NukeTestDataPanel";

const DEFAULT_TIERS = [
  { name: "Excellent", min_attendance: 95, min_on_time: 90, color: "green" },
  { name: "Strong", min_attendance: 90, min_on_time: 80, color: "teal" },
  { name: "Acceptable", min_attendance: 85, min_on_time: 70, color: "amber" },
  { name: "Needs Improvement", min_attendance: 75, min_on_time: 0, color: "orange" },
  { name: "At Risk", min_attendance: 0, min_on_time: 0, color: "red" },
];

/**
 * Lightweight collapsible card used to fold long Settings panels (Bus Staff,
 * AM/PM Care, Vendors). Each wrapper persists its open/closed state to
 * localStorage so admins don't have to re-collapse every time they revisit
 * Settings. Defaults to CLOSED so the Settings tab opens scrollable-friendly.
 */
function CollapsibleSection({ storageKey, icon: Icon, iconClassName = "text-slate-700", title, subtitle, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(`settings_collapse_${storageKey}`);
      return v === null ? defaultOpen : v === "1";
    } catch { return defaultOpen; }
  });
  useEffect(() => {
    try { localStorage.setItem(`settings_collapse_${storageKey}`, open ? "1" : "0"); } catch { /* noop */ }
  }, [open, storageKey]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`collapsible-${storageKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`collapsible-toggle-${storageKey}`}
        aria-expanded={open}
      >
        <span className="shrink-0">
          {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </span>
        {Icon && <Icon className={`w-5 h-5 ${iconClassName}`} />}
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold text-slate-900 flex items-center gap-2">
            {title}
            {badge != null && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-2 rounded-full bg-slate-900 text-white text-[11px] font-semibold tabular-nums">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</div>}
        </div>
        <span className="shrink-0 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open && <div className="border-t border-slate-100">{children}</div>}
    </div>
  );
}

const TIER_BG = {
  green: "bg-emerald-100 text-emerald-800",
  teal: "bg-teal-100 text-teal-800",
  amber: "bg-amber-100 text-amber-800",
  orange: "bg-orange-100 text-orange-800",
  red: "bg-red-600 text-white",
};

export default function Settings() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [health, setHealth] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [confirmEod, setConfirmEod] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [newSeason, setNewSeason] = useState({ name: "", short_name: "", start_date: "", end_date: "", notes: "" });
  const [showAddSeason, setShowAddSeason] = useState(false);
  const [tierDraft, setTierDraft] = useState(null);
  const [savingTiers, setSavingTiers] = useState(false);

  // Tabbed layout — persists across reloads so admins land back where they
  // left off. Each tab groups related sections so the page isn't an
  // overwhelming wall of cards.
  const TABS = [
    { key: "general", label: "General", icon: SettingsIcon },
    { key: "sync", label: "Sync & Data", icon: Database },
    { key: "seasons", label: "Seasons", icon: CalendarDays },
    { key: "performance", label: "Performance", icon: Trophy },
    { key: "devices", label: "Devices", icon: Printer },
    { key: "system", label: "System", icon: Cog },
  ];
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("rrdc_settings_tab") || "general"; } catch { return "general"; }
  });
  useEffect(() => {
    try { localStorage.setItem("rrdc_settings_tab", activeTab); } catch { /* ignore */ }
  }, [activeTab]);

  // Master email kill-switch
  const isLeadership = getLevel() === "leadership";
  const [emailToggle, setEmailToggle] = useState(null); // {enabled, source, updated_at_utc, updated_by}
  const [emailToggleBusy, setEmailToggleBusy] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null); // bool — value awaiting confirm
  const flipEmailToggle = async (enable) => {
    setEmailToggleBusy(true);
    try {
      const r = await api.post("/admin/email-toggle", { enabled: enable });
      setEmailToggle({ enabled: r.data.enabled, source: "database", updated_at_utc: r.data.updated_at_utc, updated_by: r.data.updated_by });
      toast.success(enable ? "Outbound emails ENABLED" : "Outbound emails DISABLED");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setEmailToggleBusy(false);
      setPendingToggle(null);
    }
  };

  useEffect(() => {
    if (!getToken()) return navigate("/");
    const lvl = getLevel();
    if (lvl !== "admin" && lvl !== "leadership") {
      clearSession();
      navigate("/");
    }
  }, [navigate]);

  const loadAll = async () => {
    try {
      const [cfgRes, logRes, healthRes, seasonsRes, emailRes] = await Promise.all([
        api.get("/setup/config"),
        api.get("/sync/log", { params: { limit: 100 } }),
        api.get("/health"),
        api.get("/seasons"),
        api.get("/admin/email-toggle").catch(() => ({ data: null })),
      ]);
      setConfig(cfgRes.data);
      setLogEntries(logRes.data.items || []);
      setHealth(healthRes.data);
      setSeasons(seasonsRes.data.items || []);
      setTierDraft(cfgRes.data?.performance_tiers || DEFAULT_TIERS);
      if (emailRes.data) setEmailToggle(emailRes.data);
    } catch (e) {
      toast.error("Failed to load settings");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveThresholds = async () => {
    setSavingCfg(true);
    try {
      const res = await api.patch("/setup/config", {
        late_threshold_local: config.late_threshold_local,
        absent_threshold_local: config.absent_threshold_local,
        late_arrival_threshold_local: config.late_arrival_threshold_local,
        operating_start_local: config.operating_start_local,
        operating_end_local: config.operating_end_local,
        eod_snapshot_time_local: config.eod_snapshot_time_local,
      });
      setConfig(res.data);
      toast.success("Thresholds saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingCfg(false); }
  };

  const doAction = async (path, label) => {
    try {
      await api.post(path);
      toast.success(`${label} complete`);
      loadAll();
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || "error"}`);
    }
  };

  if (!config) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading settings…</div>;
  }

  const eventColor = (t, ok) => {
    if (!ok) return "text-red-700 bg-red-50";
    if (t === "roster_sync") return "text-blue-700 bg-blue-50";
    if (t === "visits_poll") return "text-emerald-700 bg-emerald-50";
    if (t === "eod_snapshot") return "text-violet-700 bg-violet-50";
    return "text-slate-700 bg-slate-50";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => navigate("/dashboard")} className="btn-touch inline-flex items-center gap-1 px-2 sm:px-3 rounded-lg text-slate-700 hover:bg-slate-100 shrink-0" data-testid="settings-back">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-contain bg-white shadow-sm p-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-slate-500 font-bold truncate">Rolling River — Admin</div>
              <h1 className="font-display text-base sm:text-lg font-bold text-slate-900 truncate">Settings</h1>
            </div>
          </div>
          <span className="px-2 sm:px-2.5 py-1 rounded-md bg-violet-600 text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] sm:tracking-[0.16em] shrink-0">Admin</span>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Tab nav */}
        <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar pb-1" role="tablist" data-testid="settings-tab-nav">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(t.key)}
                className={`btn-touch shrink-0 inline-flex items-center gap-2 px-4 rounded-xl font-bold text-sm transition border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`settings-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid={`settings-panel-${activeTab}`}>
        {/* Public staff link */}
        {activeTab === "general" && (
        <section className="rounded-2xl border border-blue-200 bg-blue-50/50 p-4 sm:p-6 lg:col-span-2" data-testid="public-link-section">
          <div className="flex items-center gap-2 mb-1"><Link2 className="w-4 h-4 text-blue-600" /><h2 className="font-display text-lg font-bold">Public Staff Sign-In Link</h2></div>
          <p className="text-sm text-slate-600 mb-3">Share this URL with all crew. They use it every morning to sign in, sign out at the end of the day, and submit planned-absence requests. <strong>This is the source of truth for daily attendance.</strong></p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white border border-slate-300 font-mono text-sm text-slate-800 truncate" data-testid="public-link-url">
              {`${window.location.origin}/staffabsenceform`}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/staffabsenceform`);
                toast.success("Link copied to clipboard");
              }}
              className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm"
              data-testid="copy-public-link"
            >
              <Copy className="w-4 h-4" /> Copy Link
            </button>
            <a
              href="/staffabsenceform"
              target="_blank"
              rel="noreferrer"
              className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm"
              data-testid="open-public-link"
            >
              <ExternalLink className="w-4 h-4" /> Open
            </a>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            After deploy this becomes <span className="font-mono">https://3.rrdclogins.com/staffabsenceform</span>. Save it as a bookmark on the kiosk iPad and add it to your group-text or Slack pinned message.
          </div>
        </section>
        )}

        {/* (Staff roster syncs nightly from CampMinder; legacy approved-list selector removed.) */}

        {/* Season Management — leadership only */}
        {activeTab === "seasons" && isLeadership && (
          <section className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 p-6 lg:col-span-2" data-testid="season-mgmt-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-lg font-bold text-slate-900">Season Management</h2>
                <p className="text-sm text-slate-700 mt-1 max-w-2xl">
                  View the active season, edit Camp Day dates for next year, archive an ended season (downloads a full data ZIP), or open a brand-new season for the following summer. The 2026 calendar is hardcoded and read-only — clone it to start editing 2027.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => navigate("/settings/seasons")}
                  className="btn-touch px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold inline-flex items-center gap-2"
                  data-testid="open-season-mgmt"
                >
                  Open Season Manager →
                </button>
                <button
                  onClick={() => navigate("/settings/webhooks")}
                  className="btn-touch px-5 rounded-xl bg-white border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-bold inline-flex items-center gap-2"
                  data-testid="open-webhooks"
                >
                  Webhooks & Events →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Master email kill-switch (leadership only) */}
        {activeTab === "system" && emailToggle && (
          <section
            className={`rounded-2xl border p-6 lg:col-span-2 ${
              emailToggle.enabled ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"
            }`}
            data-testid="email-toggle-section"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                {emailToggle.enabled ? (
                  <Mail className="w-6 h-6 text-emerald-700 mt-0.5 shrink-0" />
                ) : (
                  <MailX className="w-6 h-6 text-rose-700 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold flex items-center gap-2 flex-wrap">
                    Outbound Email Master Switch
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded font-bold ${
                        emailToggle.enabled
                          ? "bg-emerald-600 text-white"
                          : "bg-rose-600 text-white"
                      }`}
                      data-testid="email-toggle-status"
                    >
                      {emailToggle.enabled ? "ENABLED" : "DISABLED"}
                    </span>
                  </h2>
                  <p className="text-sm text-slate-700 mt-1 max-w-2xl">
                    {emailToggle.enabled
                      ? "Camp has started. Friday tier emails, absence alerts to supervisors, and offender alerts are all LIVE."
                      : "All outbound emails are blocked (tier emails, absence alerts, offender alerts). Flip this ON the morning camp starts."}
                  </p>
                  <div className="text-xs text-slate-500 mt-2 space-x-3">
                    <span>Source: <code className="px-1 py-0.5 rounded bg-white border border-slate-200">{emailToggle.source}</code></span>
                    {emailToggle.updated_at_utc && (
                      <span>Last changed: {new Date(emailToggle.updated_at_utc).toLocaleString()}{emailToggle.updated_by ? ` by ${emailToggle.updated_by}` : ""}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isLeadership && (
                  <span className="text-xs text-slate-500 italic">Leadership-only</span>
                )}
                <button
                  onClick={() => setPendingToggle(!emailToggle.enabled)}
                  disabled={!isLeadership || emailToggleBusy}
                  className={`btn-touch px-5 rounded-xl font-bold text-white inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    emailToggle.enabled ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                  data-testid="email-toggle-btn"
                >
                  {emailToggle.enabled ? "Turn OFF" : "Turn ON"}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Email toggle confirm modal */}
        {pendingToggle !== null && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-toggle-confirm">
            <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden">
              <div className={`px-6 py-4 border-b ${pendingToggle ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                <h3 className="text-lg font-bold text-slate-900">
                  {pendingToggle ? "Enable outbound emails?" : "Disable outbound emails?"}
                </h3>
              </div>
              <div className="p-6 space-y-3 text-sm text-slate-700 leading-relaxed">
                {pendingToggle ? (
                  <>
                    <p>This will allow the system to start sending:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Real-time absence alerts to division supervisors</li>
                      <li>Sex-offender registry alerts to admin recipients</li>
                    </ul>
                    <p className="font-semibold text-rose-700">Only turn this ON once camp has officially started.</p>
                  </>
                ) : (
                  <p>All outbound emails will be blocked immediately. Use this if a bad list went out by mistake or before the season starts.</p>
                )}
              </div>
              <div className="px-6 pb-6 grid grid-cols-2 gap-2">
                <button
                  onClick={() => setPendingToggle(null)}
                  disabled={emailToggleBusy}
                  className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => flipEmailToggle(pendingToggle)}
                  disabled={emailToggleBusy}
                  className={`rounded-xl text-white font-bold py-3 disabled:opacity-50 ${pendingToggle ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
                  data-testid="email-toggle-confirm-btn"
                >
                  {emailToggleBusy ? "Saving…" : pendingToggle ? "Yes, turn ON" : "Yes, turn OFF"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Google Sheet Payroll Sync — leadership setup wizard */}
        {activeTab === "sync" && <SheetSyncPanel />}

        {/* Thresholds */}
        {activeTab === "general" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="thresholds-section">
          <h2 className="font-display text-lg font-bold mb-1">Thresholds (local time)</h2>
          <p className="text-sm text-slate-500 mb-4">Used by the dashboard and end-of-day snapshot.</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["operating_start_local", "Operating Start"],
              ["operating_end_local", "Operating End"],
              ["late_threshold_local", "Late After"],
              ["absent_threshold_local", "Absent After"],
              ["late_arrival_threshold_local", "Late-Arrival Roles Absent After"],
              ["eod_snapshot_time_local", "EOD Snapshot"],
            ].map(([key, label]) => (
              <label key={key} className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">{label}</span>
                <input
                  type="time"
                  value={config[key] || ""}
                  onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-slate-200 tabular-nums"
                  data-testid={`input-${key}`}
                />
              </label>
            ))}
          </div>
          <button onClick={saveThresholds} disabled={savingCfg} className="mt-4 btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50" data-testid="save-thresholds-btn">
            <Save className="w-4 h-4" /> {savingCfg ? "Saving…" : "Save"}
          </button>
        </section>
        )}

        {/* Late-Arrival Roles */}
        {activeTab === "performance" && (
          <CollapsibleSection
            storageKey="late_arrival"
            icon={Clock}
            iconClassName="text-amber-600"
            title="Late Arrival / Bus Staff"
            subtitle="Staff with later arrival times — different absent threshold."
          >
            <LateArrivalPanel />
          </CollapsibleSection>
        )}

        {/* AM / PM Care Roster — extra-hours staff for EOD report */}
        {activeTab === "performance" && (
          <CollapsibleSection
            storageKey="am_pm_care"
            icon={Sunrise}
            iconClassName="text-amber-500"
            title="AM / PM Care Roster"
            subtitle="Staff who stay before / after camp hours for extra pay."
          >
            <CareRosterPanel />
          </CollapsibleSection>
        )}

        {/* Outside Vendors — staff records that work for third-party vendors */}
        {activeTab === "performance" && (
          <CollapsibleSection
            storageKey="vendors"
            icon={Briefcase}
            iconClassName="text-slate-700"
            title="Outside Vendors"
            subtitle="Manually-entered vendors — bus drivers, caterers, pool service, etc."
          >
            <VendorRosterPanel />
          </CollapsibleSection>
        )}

        {activeTab === "devices" && <PrinterSetupPanel />}
        {activeTab === "devices" && (
          <CollapsibleSection
            storageKey="staff_photo_import"
            icon={Upload}
            iconClassName="text-slate-700"
            title="Staff Photo Bulk Import"
            subtitle="Upload the CampMinder Photo Report to fill in every staff card + dashboard avatar in one shot."
            defaultOpen={true}
          >
            <StaffPhotoImportPanel />
          </CollapsibleSection>
        )}
        {activeTab === "devices" && <PhotoLibraryPanel />}

        {/* Manual actions */}
        {activeTab === "sync" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="actions-section">
          <h2 className="font-display text-lg font-bold mb-1">Manual Actions</h2>
          <p className="text-sm text-slate-500 mb-4">For early-dismissal days, weather adjustments, etc.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => doAction("/sync/campminder", "Campminder sync")} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold" data-testid="action-sync-campminder">
              <RefreshCw className="w-4 h-4" /> Sync Campminder Staff Now
            </button>
            <button onClick={() => setConfirmEod(true)} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="action-eod-snapshot">
              <Lock className="w-4 h-4" /> Run EOD Snapshot
            </button>
          </div>

          <CampminderPlannedAbsencesPanel />
          {confirmEod && (
            <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-300">
              <div className="flex items-start gap-2 text-amber-900">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div>
                  <div className="font-semibold">Lock today’s record?</div>
                  <div className="text-sm">This finalizes today’s attendance and disables further automatic updates. Late-arrival sign-ins after this will still appear live but won’t change the archive.</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => { doAction("/sync/eod-snapshot", "EOD snapshot"); setConfirmEod(false); }} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm" data-testid="confirm-eod">Yes, lock it</button>
                <button onClick={() => setConfirmEod(false)} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-sm" data-testid="cancel-eod">Cancel</button>
              </div>
            </div>
          )}

          <MissingStaffDiagnostic />

          <NukeTestDataPanel />
        </section>
        )}

        {/* System health */}
        {activeTab === "system" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="health-section">
          <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-emerald-600" /><h2 className="font-display text-lg font-bold">System Health</h2></div>
          <dl className="text-sm space-y-1.5">
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">CampMinder configured</dt><dd className="font-semibold">{health?.approved_list_configured ? "Yes" : "No"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">Last CampMinder sync</dt><dd className="font-mono text-xs break-all text-right">{health?.last_roster_sync_at ? new Date(health.last_roster_sync_at).toLocaleString() : "—"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">  Staff returned</dt><dd className="font-mono">{health?.campminder_last_counts?.staff ?? "—"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">  Enrich status</dt><dd className="font-semibold">{health?.campminder_enrich_status || "—"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">Last planned-absence pull</dt><dd className="font-mono text-xs break-all text-right">{health?.planned_absences_last_pull_at ? new Date(health.planned_absences_last_pull_at).toLocaleString() : "—"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">  Absences created</dt><dd className="font-mono">{health?.planned_absences_last_stats?.created ?? "—"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">EOD snapshot today</dt><dd className="font-semibold">{health?.eod_run_today ? "Yes" : "No"}</dd></div>
            <div className="flex justify-between gap-2 flex-wrap"><dt className="text-slate-500">Server time (local)</dt><dd className="font-mono text-xs break-all text-right">{health?.server_time_local ? new Date(health.server_time_local).toLocaleString() : "—"}</dd></div>
          </dl>
        </section>
        )}

        {/* Sync log */}
        {activeTab === "sync" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="sync-log-section">
          <h2 className="font-display text-lg font-bold mb-3">Sync Log <span className="text-slate-400 font-normal text-sm">last 100 events</span></h2>
          <div className="overflow-y-auto max-h-96 border border-slate-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Time (UTC)</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Event</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logEntries.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">No events yet.</td></tr>
                )}
                {logEntries.map((e, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{e.timestamp_utc}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-[0.12em] ${eventColor(e.event_type, e.success)}`}>{e.event_type}</span>
                    </td>
                    <td className={`px-3 py-2 ${e.success ? "text-slate-700" : "text-red-700 font-semibold"}`}>{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {/* Seasons */}
        {activeTab === "seasons" && (
        <OrientationDatesPanel />
        )}

        {/* Seasons */}
        {activeTab === "seasons" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="seasons-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-blue-600" />
              <h2 className="font-display text-lg font-bold">Seasons</h2>
            </div>
            <button onClick={() => setShowAddSeason((v) => !v)} className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="add-season-btn">
              <Plus className="w-4 h-4" /> {showAddSeason ? "Close" : "Add Season"}
            </button>
          </div>

          {/* Visual timeline */}
          {seasons.length > 0 && (
            <div className="mb-4 border border-slate-200 rounded-xl p-3 bg-slate-50">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-2">Timeline</div>
              <div className="space-y-1.5">
                {(() => {
                  const sorted = [...seasons].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
                  const start = sorted[0]?.start_date;
                  const end = sorted[sorted.length - 1]?.end_date;
                  const range = (start && end) ? (new Date(end + "T12:00:00") - new Date(start + "T12:00:00")) || 1 : 1;
                  return sorted.map((s) => {
                    const sStart = new Date((s.start_date || start) + "T12:00:00");
                    const sEnd = new Date((s.end_date || end) + "T12:00:00");
                    const left = ((sStart - new Date(start + "T12:00:00")) / range) * 100;
                    const width = Math.max(2, ((sEnd - sStart) / range) * 100);
                    const c = s.status === "active" ? "bg-blue-500" : s.status === "upcoming" ? "bg-slate-300" : "bg-slate-400";
                    return (
                      <div key={s.id || s._id} className="flex items-center gap-2 text-xs">
                        <span className="w-32 truncate font-semibold text-slate-700">{s.name}</span>
                        <div className="flex-1 h-2.5 rounded bg-slate-100 relative overflow-hidden">
                          <div className={`absolute h-full ${c} rounded`} style={{ left: `${left}%`, width: `${width}%` }} />
                        </div>
                        <span className="w-44 text-right font-mono tabular-nums text-slate-500">{s.start_date} → {s.end_date}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {showAddSeason && (
            <div className="border border-slate-200 rounded-xl p-4 mb-4 bg-blue-50/40">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <input value={newSeason.name} onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })} placeholder="Name (e.g., Summer 2027)" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-season-name" />
                <input value={newSeason.short_name} onChange={(e) => setNewSeason({ ...newSeason, short_name: e.target.value })} placeholder="Short (S27)" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-season-short" />
                <input type="date" value={newSeason.start_date} onChange={(e) => setNewSeason({ ...newSeason, start_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-season-start" />
                <input type="date" value={newSeason.end_date} onChange={(e) => setNewSeason({ ...newSeason, end_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="new-season-end" />
                <input value={newSeason.notes} onChange={(e) => setNewSeason({ ...newSeason, notes: e.target.value })} placeholder="Notes" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </div>
              <button
                onClick={async () => {
                  try {
                    await api.post("/seasons", newSeason);
                    toast.success("Season created");
                    setNewSeason({ name: "", short_name: "", start_date: "", end_date: "", notes: "" });
                    setShowAddSeason(false);
                    loadAll();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
                }}
                className="mt-3 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                data-testid="new-season-save"
              >
                Create Season
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Name</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Short</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Start</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">End</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Days</th>
                  <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {seasons.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No seasons yet.</td></tr>
                )}
                {seasons.map((s) => {
                  const sid = s.id || s._id;
                  const stColor = s.status === "active" ? "bg-emerald-100 text-emerald-800" : s.status === "upcoming" ? "bg-blue-100 text-blue-800" : "bg-slate-200 text-slate-700";
                  return (
                    <tr key={sid} className="border-t border-slate-100" data-testid={`season-row-${sid}`}>
                      <td className="px-3 py-2">
                        <input
                          defaultValue={s.name}
                          onBlur={async (e) => {
                            if (e.target.value !== s.name) {
                              try { await api.patch(`/seasons/${sid}`, { name: e.target.value }); toast.success("Saved"); loadAll(); } catch { toast.error("Save failed"); }
                            }
                          }}
                          className="px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-blue-300 outline-none w-full"
                          data-testid={`season-name-${sid}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono">{s.short_name}</td>
                      <td className="px-3 py-2 font-mono tabular-nums">
                        <input type="date" defaultValue={s.start_date} onBlur={async (e) => {
                          if (e.target.value !== s.start_date) { try { await api.patch(`/seasons/${sid}`, { start_date: e.target.value }); loadAll(); } catch { toast.error("Save failed"); } }
                        }} className="px-2 py-1 rounded border border-transparent hover:border-slate-200" />
                      </td>
                      <td className="px-3 py-2 font-mono tabular-nums">
                        <input type="date" defaultValue={s.end_date} onBlur={async (e) => {
                          if (e.target.value !== s.end_date) { try { await api.patch(`/seasons/${sid}`, { end_date: e.target.value }); loadAll(); } catch { toast.error("Save failed"); } }
                        }} className="px-2 py-1 rounded border border-transparent hover:border-slate-200" />
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-[0.12em] ${stColor}`}>{s.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{s.stats?.days_archived || 0} arch / {s.stats?.days_live || 0} live</td>
                      <td className="px-3 py-2 text-right">
                        {s.status !== "archived" ? (
                          <button onClick={async () => {
                            if (!window.confirm(`Archive "${s.name}"?`)) return;
                            try { await api.post(`/seasons/${sid}/archive`); toast.success("Archived"); loadAll(); } catch { toast.error("Archive failed"); }
                          }} className="px-3 py-1 rounded bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50" data-testid={`archive-season-${sid}`}>Archive</button>
                        ) : (
                          <button onClick={async () => {
                            try { await api.post(`/seasons/${sid}/activate`); toast.success("Activated"); loadAll(); } catch { toast.error("Activate failed"); }
                          }} className="px-3 py-1 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700" data-testid={`activate-season-${sid}`}>Activate</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {/* Performance Tiers */}
        {activeTab === "performance" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="tiers-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-600" />
              <h2 className="font-display text-lg font-bold">Performance Tiers</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTierDraft(DEFAULT_TIERS)}
                className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold"
                data-testid="tiers-reset"
              >
                <RotateCcw className="w-4 h-4" /> Reset Defaults
              </button>
              <button
                onClick={async () => {
                  setSavingTiers(true);
                  try {
                    await api.patch("/setup/config", { performance_tiers: tierDraft });
                    toast.success("Tier thresholds saved");
                    loadAll();
                  } catch { toast.error("Save failed"); }
                  finally { setSavingTiers(false); }
                }}
                disabled={savingTiers}
                className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="tiers-save"
              >
                <Save className="w-4 h-4" /> {savingTiers ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-3">Tiers are evaluated top-down. The first tier where a staff member meets BOTH the attendance and on-time minimums wins.</p>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Tier</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Min Attendance %</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Min On-Time %</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Color</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Preview</th>
              </tr>
            </thead>
            <tbody>
              {(tierDraft || []).map((t, idx) => (
                <tr key={t.name} className="border-t border-slate-100" data-testid={`tier-row-${t.color}`}>
                  <td className="px-3 py-2 font-semibold">{t.name}</td>
                  <td className="px-3 py-2">
                    <input type="number" min={0} max={100} value={t.min_attendance}
                      onChange={(e) => { const draft = [...tierDraft]; draft[idx] = { ...t, min_attendance: parseInt(e.target.value || 0, 10) }; setTierDraft(draft); }}
                      className="w-20 px-2 py-1 rounded border border-slate-200 tabular-nums"
                      data-testid={`tier-min-att-${t.color}`} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min={0} max={100} value={t.min_on_time}
                      onChange={(e) => { const draft = [...tierDraft]; draft[idx] = { ...t, min_on_time: parseInt(e.target.value || 0, 10) }; setTierDraft(draft); }}
                      className="w-20 px-2 py-1 rounded border border-slate-200 tabular-nums"
                      data-testid={`tier-min-ot-${t.color}`} />
                  </td>
                  <td className="px-3 py-2 capitalize text-slate-600">{t.color}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[11px] font-bold ${TIER_BG[t.color] || ""}`}>{t.name}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        )}

        {activeTab === "performance" && <RafflesPanel />}
        {activeTab === "performance" && <TierEmailsPanel />}
        </div>
      </main>
    </div>
  );
}
